/**
 * AIResolveModal — Progress modal for AI Resolve / AI Resolve All.
 *
 * Single-issue mode: shows 5 progress steps (fetch → analyze → apply → save → rerun).
 * Batch mode: shows a progress bar and scrolling per-issue log.
 *
 * Props:
 *   resolveState         — state object from useAIResolve
 *   onClose              — () → void
 *   onCompleted          — async (newJobId) → void — called when rerun finishes; awaited before Close is enabled
 *   onOpenFixAndContinue — (issue) → void — fallback on error
 */

import { useState, useEffect } from 'react'
import { Wand2, CheckCircle2, Circle, Loader2, AlertTriangle, X } from 'lucide-react'
import DraggableModal from './DraggableModal'
import './AIResponseModal.css'
import './AIResolveModal.css'

const STEPS = [
  { key: 'fetching',   label: 'Fetching file content' },
  { key: 'analyzing',  label: 'Analyzing with Claude' },
  { key: 'applying',   label: 'Applying patch' },
  { key: 'saving',     label: 'Saving to S3' },
  { key: 'rerunning',  label: 'Re-running compile check' },
]

const STEP_ORDER = STEPS.map(s => s.key)

function stepStatus(stepKey, currentStep) {
  if (currentStep === 'done')  return 'done'
  if (currentStep === 'error') {
    const idx = STEP_ORDER.indexOf(stepKey)
    const cur = STEP_ORDER.indexOf(currentStep)
    return idx < cur ? 'done' : idx === cur ? 'error' : 'pending'
  }
  const idx = STEP_ORDER.indexOf(stepKey)
  const cur = STEP_ORDER.indexOf(currentStep)
  if (idx < cur)  return 'done'
  if (idx === cur) return 'active'
  return 'pending'
}

function StepIcon({ status }) {
  if (status === 'done')    return <CheckCircle2 size={14} className="step-icon done" />
  if (status === 'active')  return <Loader2 size={14} className="step-icon spinning" />
  if (status === 'error')   return <AlertTriangle size={14} className="step-icon error" />
  return <Circle size={14} className="step-icon pending" />
}

export default function AIResolveModal({ resolveState, onClose, onCompleted, onOpenFixAndContinue }) {
  const [refreshing, setRefreshing] = useState(false)

  const { mode, step, currentIssue, batchTotal, batchDone, batchLog, errorMessage, newJobId, summary,
          beforeErrors, afterErrors, rolledBack } = resolveState ?? {}

  const isBatch      = mode === 'batch'
  const isDone       = step === 'done'
  const isError      = step === 'error'
  const showFallback = isDone || isError

  // Derived batch counts (computed from log so modal is always in sync with actual outcomes)
  const appliedCount = (batchLog || []).filter(e => e.status === 'ok').length
  const failedCount  = (batchLog || []).filter(e => e.status === 'error').length

  // When rerun completes (step='done'), call onCompleted and block Close until refresh finishes.
  // For step='error', no rerun happened — Close is available immediately.
  useEffect(() => {
    if (step !== 'done' || !onCompleted) return
    if (import.meta.env.DEV)
      console.log(' [AIResolveModal] step=done → calling onCompleted', { updatedGateJobId: newJobId, summary, rolledBack })
    let cancelled = false
    setRefreshing(true)
    Promise.resolve(onCompleted({ updatedGateJobId: newJobId, summary, rolledBack: rolledBack || [] })).finally(() => {
      if (!cancelled) setRefreshing(false)
    })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  if (!resolveState?.open) return null

  const title = isBatch ? 'AI Resolve All' : 'AI Resolve'
  const issueFile = currentIssue?.javaLocation?.file || ''
  const issueLine = currentIssue?.javaLocation?.line || ''

  return (
    <div className="ai-modal-overlay" onClick={refreshing ? undefined : onClose}>
      <DraggableModal
        className="ai-modal ai-resolve-modal"
        onClose={refreshing ? undefined : onClose}
        onClick={e => e.stopPropagation()}
      >

        {/* ── Header ── */}
        <div className="ai-modal-header">
          <div className="ai-modal-title">
            <Wand2 size={16} />
            <span>{title}</span>
          </div>

          {!isBatch && issueFile && (
            <div className="ais-context-chip">
              <code>{issueFile}</code>
              {issueLine && <span>:{issueLine}</span>}
            </div>
          )}

          {isBatch && (
            <div className="ai-resolve-batch-count">
              {batchDone} / {batchTotal} file{batchTotal !== 1 ? 's' : ''}
            </div>
          )}

          <button className="ai-modal-close" onClick={onClose} disabled={refreshing} title="Close">
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="ai-modal-content ai-resolve-body">

          {/* Issue summary (single mode) */}
          {!isBatch && currentIssue?.summary && (
            <div className="ais-issue-banner">
              <AlertTriangle size={12} />
              <span>{currentIssue.summary}</span>
            </div>
          )}

          {/* Single-issue progress steps */}
          {!isBatch && (
            <div className="ai-resolve-steps">
              {STEPS.map(({ key, label }) => {
                const status = stepStatus(key, step)
                return (
                  <div key={key} className={`ai-resolve-step ${status}`}>
                    <StepIcon status={status} />
                    <span>{label}{status === 'active' ? '…' : ''}</span>
                  </div>
                )
              })}
            </div>
          )}

          {/* Batch progress bar + log */}
          {isBatch && (
            <>
              <div className="ai-resolve-progress-wrap">
                <div className="ai-resolve-progress-bar">
                  <div
                    className="ai-resolve-progress-fill"
                    style={{ width: `${batchTotal ? Math.round((batchDone / batchTotal) * 100) : 0}%` }}
                  />
                </div>
                <span className="ai-resolve-progress-label">
                  {batchDone} / {batchTotal}
                </span>
              </div>

              {/* Batch current step (rerunning shows separately) */}
              {step === 'rerunning' && (
                <div className="ai-resolve-step active">
                  <Loader2 size={14} className="step-icon spinning" />
                  <span>Re-running compile check…</span>
                </div>
              )}

              {/* Batch log — one entry per FILE (not per error) */}
              <div className="ai-resolve-batch-log">
                {batchLog.map((entry, i) => (
                  <div key={i} className={`ai-resolve-batch-entry ${entry.status}`}>
                    {entry.status === 'ok'
                      ? <CheckCircle2 size={12} className="step-icon done" />
                      : <AlertTriangle size={12} className="step-icon error" />
                    }
                    <span className="entry-file">
                      {entry.filename || entry.issue?.javaLocation?.file}
                    </span>
                    <span className="entry-msg">
                      — {entry.message}
                      {entry.errorCount > 1 && entry.status === 'ok' && ` (${entry.errorCount} errors)`}
                    </span>
                  </div>
                ))}

                {/* Currently processing */}
                {step && step !== 'rerunning' && step !== 'done' && step !== 'error' && currentIssue && (
                  <div className="ai-resolve-batch-entry processing">
                    <Loader2 size={12} className="step-icon spinning" />
                    <span className="entry-file">
                      {currentIssue.javaLocation?.file}
                    </span>
                    <span className="entry-msg">— {step}…</span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Error message */}
          {isError && errorMessage && (
            <div className="ais-error" style={{ marginTop: 8 }}>
              <AlertTriangle size={13} />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Done / refreshing message */}
          {isDone && refreshing && (
            <div className="ai-resolve-done-msg">
              <Loader2 size={14} className="step-icon spinning" />
              Refreshing report…
            </div>
          )}

          {isDone && !refreshing && !isBatch && (
            <div className="ai-resolve-done-msg">
              <CheckCircle2 size={14} />
              Compile check complete — issue list updated.
            </div>
          )}

          {isDone && !refreshing && isBatch && (
            <div className="ai-resolve-done-msg">
              {rolledBack?.length > 0 ? (
                <>
                  <AlertTriangle size={14} className="step-icon error" style={{ color: 'var(--accent-red)' }} />
                  <span>
                    AI couldn&apos;t fix this safely — your file is unchanged ({beforeErrors ?? rolledBack.length} error{(beforeErrors ?? 1) !== 1 ? 's' : ''}).
                    {' '}Click &quot;Open Fix &amp; Continue&quot; to fix manually.
                  </span>
                </>
              ) : (
                <>
                  <CheckCircle2 size={14} />
                  <span>
                    {afterErrors != null && beforeErrors != null && afterErrors < beforeErrors
                      ? `Fixed! Errors: ${beforeErrors} → ${afterErrors} (${beforeErrors - afterErrors} resolved).`
                      : afterErrors != null && beforeErrors != null && afterErrors === 0
                        ? 'All errors resolved! Compile check passed.'
                        : `Patches applied. Recompile complete.${beforeErrors != null && afterErrors != null ? ` Errors: ${beforeErrors} → ${afterErrors}.` : ''}`
                    }
                    {' '}Issue list updated.
                  </span>
                </>
              )}
            </div>
          )}

        </div>

        {/* ── Footer ── */}
        <div className="ai-modal-footer">
          <div className="ai-modal-footer-right">
            {showFallback && (isError || (isDone && currentIssue)) && (
              <button
                className="ai-modal-btn secondary"
                disabled={refreshing}
                onClick={() => {
                  // For batch partial/error, open the first failed issue
                  const firstFailed = (batchLog || []).find(e => e.status === 'error')
                  onOpenFixAndContinue?.(firstFailed?.issue || currentIssue)
                }}
                title={refreshing ? 'Refreshing report — please wait…' : 'Open Fix & Continue for the first unresolved issue'}
              >
                Open Fix &amp; Continue
              </button>
            )}
            <button className="ai-modal-btn" onClick={onClose} disabled={refreshing}>
              {refreshing ? 'Refreshing…' : 'Close'}
            </button>
          </div>
        </div>

      </DraggableModal>
    </div>
  )
}
